import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    TextField,
    Grid,
    Card,
    CardContent,
    InputAdornment,
    MenuItem
} from '@mui/material';
import {
    Warning as WarningIcon,
    Payment as PaymentIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Visibility as VisibilityIcon,
    Send as SendIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getOverdueAccounts, recordPayment } from '../../services/api';
import './Credit.css';

const OverdueAccounts = () => {
    const [overdueData, setOverdueData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [chequeNumber, setChequeNumber] = useState('');
    const [bankName, setBankName] = useState('');
    const [processingPayment, setProcessingPayment] = useState(false);
    const [daysFilter, setDaysFilter] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        fetchOverdueAccounts();
    }, []);

    const fetchOverdueAccounts = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await getOverdueAccounts();
            setOverdueData(response.data);
        } catch (err) {
            setError(err.message || 'Failed to load overdue accounts');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPaymentDialog = (account) => {
        setSelectedAccount(account);
        const amount = parseFloat(account.outstanding_balance.replace(/[^0-9.-]+/g, "")) || 0;
        setPaymentAmount(amount.toString());
        setPaymentMethod('cash');
        setChequeNumber('');
        setBankName('');
        setPaymentDialogOpen(true);
    };

    const handleProcessPayment = async () => {
        if (!selectedAccount || !paymentAmount) return;
        
        setProcessingPayment(true);
        try {
            await recordPayment({
                customer_id: selectedAccount.customer_id,
                amount: parseFloat(paymentAmount),
                payment_method: paymentMethod,
                cheque_number: paymentMethod === 'cheque' ? chequeNumber : null,
                bank_name: paymentMethod === 'bank_transfer' ? bankName : null,
                description: `Payment for overdue account - ${selectedAccount.account_number}`
            });
            
            // Refresh data
            await fetchOverdueAccounts();
            setPaymentDialogOpen(false);
            
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to process payment');
        } finally {
            setProcessingPayment(false);
        }
    };

    const formatCurrency = (amount) => {
        if (typeof amount === 'string' && amount.includes('LKR')) return amount;
        return new Intl.NumberFormat('en-LK', {
            style: 'currency',
            currency: 'LKR',
            minimumFractionDigits: 2
        }).format(parseFloat(amount) || 0);
    };

    const getFilteredAccounts = () => {
        if (!overdueData?.accounts) return [];
        
        let filtered = overdueData.accounts;
        
        if (daysFilter !== 'all') {
            const days = parseInt(daysFilter);
            filtered = filtered.filter(account => account.days_overdue >= days);
        }
        
        return filtered;
    };

    const sendReminder = async (account) => {
        // Implement email/SMS reminder functionality
        alert(`Reminder sent to ${account.customer_name}`);
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4" component="h1">
                        <WarningIcon sx={{ mr: 2, verticalAlign: 'middle', color: 'warning.main' }} />
                        Overdue Accounts
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchOverdueAccounts}
                    >
                        Refresh
                    </Button>
                </Box>
                <Typography variant="subtitle1" color="textSecondary">
                    Manage and track overdue credit accounts
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderLeft: '4px solid #f44336' }}>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Total Overdue Accounts
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="error">
                                {overdueData?.total_overdue || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Total Overdue Amount
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="warning.main">
                                {overdueData?.total_amount || 'LKR 0.00'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderLeft: '4px solid #9c27b0' }}>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Average Days Overdue
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                                {overdueData?.accounts?.length 
                                    ? Math.round(overdueData.accounts.reduce((sum, acc) => sum + acc.days_overdue, 0) / overdueData.accounts.length)
                                    : 0
                                } days
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Accounts &gt; 60 days
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="error">
                                {overdueData?.accounts?.filter(acc => acc.days_overdue > 60).length || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            select
                            label="Filter by Days Overdue"
                            value={daysFilter}
                            onChange={(e) => setDaysFilter(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterListIcon />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            <MenuItem value="all">All Overdue</MenuItem>
                            <MenuItem value="30">30+ days</MenuItem>
                            <MenuItem value="60">60+ days</MenuItem>
                            <MenuItem value="90">90+ days</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={8} sx={{ textAlign: 'right' }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {/* Export functionality */}}
                            sx={{ mr: 1 }}
                        >
                            Export Report
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon />}
                            onClick={() => {/* Send bulk reminders */}}
                        >
                            Send Bulk Reminders
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Overdue Accounts Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Customer</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell align="right">Overdue Amount</TableCell>
                            <TableCell align="center">Days Overdue</TableCell>
                            <TableCell align="center">Credit Status</TableCell>
                            <TableCell align="center">Last Payment</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getFilteredAccounts().map((account) => (
                            <TableRow 
                                key={account.id} 
                                hover
                                sx={{ 
                                    backgroundColor: account.days_overdue > 90 ? '#ffebee' : 
                                                    account.days_overdue > 60 ? '#fff3e0' : 'inherit'
                                }}
                            >
                                <TableCell>
                                    <Box>
                                        <Typography variant="body1" fontWeight="medium">
                                            {account.customer_name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {account.customer_code}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {account.phone && (
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <PhoneIcon fontSize="small" />
                                                <Typography variant="body2">
                                                    {account.phone}
                                                </Typography>
                                            </Box>
                                        )}
                                        {account.email && (
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <EmailIcon fontSize="small" />
                                                <Typography variant="body2">
                                                    {account.email}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1" color="error" fontWeight="bold">
                                        {account.outstanding_balance}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip 
                                        label={`${account.days_overdue} days`}
                                        size="small"
                                        color={
                                            account.days_overdue > 90 ? 'error' :
                                            account.days_overdue > 60 ? 'warning' : 'default'
                                        }
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip 
                                        label={account.credit_status.toUpperCase()}
                                        size="small"
                                        color={
                                            account.credit_status === 'over_limit' ? 'error' :
                                            account.credit_status === 'blocked' ? 'error' : 'default'
                                        }
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {account.last_payment_date 
                                            ? new Date(account.last_payment_date).toLocaleDateString()
                                            : 'Never'
                                        }
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Box display="flex" justifyContent="center" gap={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate(`/credit/customers/${account.customer_id}`)}
                                            title="View Details"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenPaymentDialog(account)}
                                            title="Record Payment"
                                        >
                                            <PaymentIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="warning"
                                            onClick={() => sendReminder(account)}
                                            title="Send Reminder"
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        
                        {getFilteredAccounts().length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <Typography color="textSecondary">
                                        {overdueData?.total_overdue === 0 
                                            ? 'No overdue accounts' 
                                            : 'No accounts match your filter'
                                        }
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Summary Section */}
            {getFilteredAccounts().length > 0 && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" gutterBottom>
                                Summary for Filtered Accounts
                            </Typography>
                            <Typography variant="body2">
                                Total Accounts: {getFilteredAccounts().length}
                            </Typography>
                            <Typography variant="body2">
                                Total Amount: {formatCurrency(
                                    getFilteredAccounts().reduce((sum, acc) => 
                                        sum + parseFloat(acc.outstanding_balance.replace(/[^0-9.-]+/g, "")), 0
                                    )
                                )}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => {
                                    // Implement bulk action
                                }}
                                sx={{ mr: 1 }}
                            >
                                Block All &gt; 90 Days
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    // Implement follow-up action
                                }}
                            >
                                Schedule Follow-up
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Payment Dialog */}
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogContent>
                    {selectedAccount && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Customer: <strong>{selectedAccount.customer_name}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Account: {selectedAccount.account_number}
                            </Typography>
                            
                            <Box sx={{ my: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2">Current Overdue Amount:</Typography>
                                <Typography variant="h5" color="error" fontWeight="bold">
                                    {selectedAccount.outstanding_balance}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Days Overdue: {selectedAccount.days_overdue} days
                                </Typography>
                            </Box>
                            
                            <TextField
                                fullWidth
                                label="Payment Amount (LKR)"
                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                margin="normal"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                                }}
                                helperText={`Maximum: ${selectedAccount.outstanding_balance}`}
                            />
                            
                            <TextField
                                fullWidth
                                select
                                label="Payment Method"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="cheque">Cheque</MenuItem>
                                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                <MenuItem value="card">Credit/Debit Card</MenuItem>
                                <MenuItem value="online">Online Payment</MenuItem>
                            </TextField>
                            
                            {paymentMethod === 'cheque' && (
                                <TextField
                                    fullWidth
                                    label="Cheque Number"
                                    value={chequeNumber}
                                    onChange={(e) => setChequeNumber(e.target.value)}
                                    margin="normal"
                                />
                            )}
                            
                            {paymentMethod === 'bank_transfer' && (
                                <TextField
                                    fullWidth
                                    label="Bank Name"
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value)}
                                    margin="normal"
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialogOpen(false)} disabled={processingPayment}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleProcessPayment} 
                        variant="contained" 
                        disabled={processingPayment || !paymentAmount}
                        startIcon={processingPayment ? <CircularProgress size={20} /> : <PaymentIcon />}
                    >
                        {processingPayment ? 'Processing...' : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default OverdueAccounts;